import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { useWhiteboardShapes } from '../hooks/useWhiteboardShapes';
import { useWhiteboardText }   from '../hooks/useWhiteboardText';

// ─── Icônes SVG inline ────────────────────────────────────────────────────────

const Icon = ({ d, size = 17, color = 'currentColor', style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24"
    fill="none" stroke={color} strokeWidth="1.75"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ display: 'block', flexShrink: 0, pointerEvents: 'none', ...style }}
    aria-hidden="true">
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p}/>) : <path d={d}/>}
  </svg>
);

const ICONS = {
  pencil:      ['M4 20h4l10.5-10.5a2 2 0 0 0-4-4L4 16v4z','M13.5 6.5l4 4'],
  infoCircle:  ['M12 9h.01','M11 12h1v4h1','M12 3a9 9 0 1 0 0 18A9 9 0 0 0 12 3'],
  user:        ['M8 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0','M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2'],
  x:           ['M18 6 6 18','M6 6l12 12'],
  world:       ['M12 3a9 9 0 1 0 0 18A9 9 0 0 0 12 3','M3.6 9h16.8','M3.6 15h16.8','M11.5 3a17 17 0 0 0 0 18','M12.5 3a17 17 0 0 1 0 18'],
  lock:        ['M5 13a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-6z','M11 17a1 1 0 1 0 2 0 1 1 0 0 0-2 0','M8 11V7a4 4 0 1 1 8 0v4'],
  cursor:      ['M6 4l12 7-7 1-3 7L6 4z'],
  eraser:      ['M19 20H8.5l-4.21-4.3a1 1 0 0 1 0-1.41l10-10a1 1 0 0 1 1.41 0l5 5a1 1 0 0 1 0 1.41L11 20','M18 13.3l-6.3-6.29'],
  arrowBackUp: ['M9 14 4 9l5-5','M4 9h10.5a5.5 5.5 0 0 1 0 11H11'],
  scissors:    ['M6 7a3 3 0 1 0 6 0A3 3 0 0 0 6 7','M6 17a3 3 0 1 0 6 0A3 3 0 0 0 6 17','M8.6 8.6 19 19','M19 5 8.6 15.4'],
  photoPlus:   ['M15 8h.01','M4 15l4-4 4 4 3-3 3 3','M3 6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6z','M16 19h6','M19 16v6'],
  trash:       ['M4 7h16','M10 11v6','M14 11v6','M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12','M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3'],
  fileMinus:   ['M14 3v4a1 1 0 0 0 1 1h4','M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z','M9 14h6'],
  floppy:      ['M6 4h10l4 4v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2','M10 14a2 2 0 1 0 4 0 2 2 0 0 0-4 0','M8 4v4h6V4'],
  shapes:      ['M3 5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5z','M13 17a4 4 0 1 0 8 0 4 4 0 0 0-8 0','M3.5 20.5l4-7'],
  rect:        ['M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5z'],
  circle:      ['M12 3a9 9 0 1 0 0 18A9 9 0 0 0 12 3'],
  line:        ['M5 19L19 5'],
  arrow:       ['M5 19L19 5','M9 5h10v10'],
  chevronDown: ['M6 9l6 6 6-6'],
  textCursor:  ['M13 20h-2a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h2','M7 4h2','M15 4h2','M7 20h2','M15 20h2'],
  bold:        ['M7 5h6a3 3 0 0 1 0 6H7V5z','M7 11h7a3 3 0 0 1 0 6H7v-6z'],
  italic:      ['M11 5h4','M7 19h4','M10 5l-4 14'],
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
  wrap: {
    background: 'var(--bg-secondary, #f5f5f3)', borderRadius: 12,
    border: '1px solid rgba(0,0,0,.1)', padding: '14px 16px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 },
  iconBox: { width: 36, height: 36, borderRadius: 8, background: '#dbeafe',
             display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  titleRow:  { display: 'flex', alignItems: 'center', gap: 6 },
  titleText: { fontWeight: 600, fontSize: 15, color: 'var(--text-color, #222)', lineHeight: 1.2 },
  infoBtn:   { background: 'none', border: 'none', padding: 2, cursor: 'pointer',
               display: 'flex', alignItems: 'center', borderRadius: 4 },
  subtitle:  { fontSize: 12, color: '#888', marginTop: 3, overflow: 'hidden',
               textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260,
               display: 'flex', alignItems: 'center', gap: 4 },
  visRow: { display: 'flex', gap: 6, marginBottom: 12 },
  visBtn: (active, variant) => ({
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '4px 11px', borderRadius: 99, fontSize: 12,
    cursor: 'pointer', fontFamily: 'inherit',
    border: active ? (variant === 'shared' ? '1px solid #93c5fd' : '1px solid #d1d5db') : '1px solid #d1d5db',
    background: active ? (variant === 'shared' ? '#dbeafe' : '#f3f4f6') : 'none',
    color: active ? (variant === 'shared' ? '#1d4ed8' : '#555') : '#666',
    transition: 'all .15s',
  }),
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
    padding: '6px 10px', background: '#fff', borderRadius: 8,
    border: '1px solid rgba(0,0,0,.09)', position: 'relative',
  },
  sep: { width: 1, height: 20, background: 'rgba(0,0,0,.1)', margin: '0 5px', flexShrink: 0 },
  tbBtn: (active, danger, hov) => ({
    background: active ? '#dbeafe' : hov ? (danger ? '#fee2e2' : '#f3f4f6') : 'none',
    border: 'none', cursor: 'pointer', width: 30, height: 30, borderRadius: 7,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: active ? '#1d4ed8' : danger ? '#dc2626' : hov ? '#111' : '#555',
    transition: 'background .12s, color .12s', flexShrink: 0, padding: 0,
  }),
  colorDot: (hex, active) => ({
    width: 18, height: 18, borderRadius: '50%', background: hex,
    border: '2px solid transparent',
    outline: active ? '2px solid rgba(0,0,0,.35)' : 'none', outlineOffset: 2,
    cursor: 'pointer', flexShrink: 0, transition: 'transform .1s, outline .1s',
    display: 'inline-block', boxSizing: 'border-box',
  }),
  select: (maxWidth) => ({
    fontSize: 12, padding: '4px 7px', borderRadius: 7,
    border: '1px solid rgba(0,0,0,.15)', background: '#fff', color: '#222',
    cursor: 'pointer', maxWidth: maxWidth || 130, minWidth: 0, fontFamily: 'inherit',
  }),
  saveBtn: (hov) => ({
    display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 7,
    border: '1px solid #86efac', background: hov ? '#dcfce7' : 'none', color: '#166534',
    fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
    transition: 'background .15s',
  }),
  rightGroup: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2 },
  dropBtn: (active, hov) => ({
    display: 'flex', alignItems: 'center', gap: 3,
    padding: '0 6px 0 4px', height: 30, borderRadius: 7, border: 'none', cursor: 'pointer',
    background: active ? '#dbeafe' : hov ? '#f3f4f6' : 'none',
    color: active ? '#1d4ed8' : hov ? '#111' : '#555',
    transition: 'background .12s, color .12s', flexShrink: 0,
  }),
  dropMenu: {
    position: 'absolute', top: 'calc(100% + 6px)', left: 0,
    background: '#fff', border: '1px solid rgba(0,0,0,.12)', borderRadius: 10,
    boxShadow: '0 4px 16px rgba(0,0,0,.1)', zIndex: 200,
  },
  shapeGrid: { display: 'flex', gap: 4, padding: 8 },
  shapeItem: (active, hov) => ({
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    padding: '8px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
    background: active ? '#dbeafe' : hov ? '#f3f4f6' : 'none',
    color: active ? '#1d4ed8' : '#555', fontSize: 11, fontFamily: 'inherit',
    transition: 'background .12s', minWidth: 52,
  }),
  textMenuInner: { display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 12px', minWidth: 180 },
  textLabel: { fontSize: 11, color: '#888', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.04em' },
  toggleBtn: (active, hov) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, borderRadius: 7, border: 'none', cursor: 'pointer',
    background: active ? '#dbeafe' : hov ? '#f3f4f6' : 'none',
    color: active ? '#1d4ed8' : '#555', transition: 'background .12s', fontFamily: 'inherit',
  }),
  fontSizeBtn: (active, hov) => ({
    padding: '3px 8px', borderRadius: 6, border: 'none', cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 12,
    background: active ? '#dbeafe' : hov ? '#f3f4f6' : 'none',
    color: active ? '#1d4ed8' : '#444', fontWeight: active ? 600 : 400,
    transition: 'background .12s',
  }),
};

// ─── Données ──────────────────────────────────────────────────────────────────

const COLORS = [
  { hex: '#1a1a1a', label: 'Noir'  },
  { hex: '#dc2626', label: 'Rouge' },
  { hex: '#16a34a', label: 'Vert'  },
  { hex: '#2563eb', label: 'Bleu'  },
];

const SHAPES = [
  { key: 'rect',   label: 'Rectangle', icon: 'rect'   },
  { key: 'circle', label: 'Cercle',    icon: 'circle' },
  { key: 'line',   label: 'Ligne',     icon: 'line'   },
  { key: 'arrow',  label: 'Flèche',    icon: 'arrow'  },
];

const FONT_SIZES = [12, 16, 20, 28, 36];

// ─── Petits sous-composants ───────────────────────────────────────────────────

function ToolButton({ iconKey, label, active = false, danger = false, onClick }) {
  const [hov, setHov] = useState(false);
  const st = S.tbBtn(active, danger, hov);
  return (
    <button style={st} aria-label={label} title={label}
      aria-pressed={active || undefined} onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}>
      <Icon d={ICONS[iconKey]} size={17} color={st.color}/>
    </button>
  );
}

function ShapeItemBtn({ shape, active, onClick }) {
  const [hov, setHov] = useState(false);
  const st = S.shapeItem(active, hov);
  return (
    <button style={st} aria-label={shape.label} title={shape.label} role="menuitem"
      onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}>
      <Icon d={ICONS[shape.icon]} size={20} color={st.color}/>
      {shape.label}
    </button>
  );
}

function FontSizeBtn({ size, active, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button style={S.fontSizeBtn(active, hov)} onClick={() => onClick(size)}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}>
      {size}
    </button>
  );
}

function ToggleBtn({ iconKey, label, active, onClick }) {
  const [hov, setHov] = useState(false);
  const st = S.toggleBtn(active, hov);
  return (
    <button style={st} aria-label={label} title={label} aria-pressed={active}
      onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}>
      <Icon d={ICONS[iconKey]} size={16} color={st.color}/>
    </button>
  );
}

// Hook générique : ferme un menu flottant si clic hors du ref + hors du trigger
function useOutsideClick(ref, triggerAttr, onClose, enabled) {
  useEffect(() => {
    if (!enabled) return;
    const id = setTimeout(() => {
      const handler = (e) => {
        if (ref.current?.contains(e.target)) return;
        if (triggerAttr && e.target.closest?.(`[${triggerAttr}]`)) return;
        onClose();
      };
      document.addEventListener('mousedown', handler);
      ref.current.__outsideHandler = handler;
    }, 0);
    return () => {
      clearTimeout(id);
      if (ref.current?.__outsideHandler) {
        document.removeEventListener('mousedown', ref.current.__outsideHandler);
        delete ref.current.__outsideHandler;
      }
    };
  }, [enabled, onClose]);
}

// ─── Composant principal ──────────────────────────────────────────────────────

const WhiteboardToolbar = ({
  canvasRef, socket, discussionId, discussionName,
  visibility, onSetVisibility,
  createdBy, userId, createur, setCreateur,
  whiteboardList, currentName,
  onSelect, onNew, onDelete, onSave,
  loadImage, onSetEraser, onSetPencil, onUndo, onClose,
  prompt,
}) => {
  const { t } = useTranslation();
  const fileInputRef   = useRef(null);
  // ── Ref clé : survit au blur causé par un clic sur la toolbar ─────────────
  const activeITextRef = useRef(null);

  // ── État global du mode actif ─────────────────────────────────────────────
  const [mode, setMode] = useState('draw');

  // ── État UI ───────────────────────────────────────────────────────────────
  const [currentWhiteboardId, setCurrentWhiteboardId] = useState(null);
  const [activeColor,         setActiveColor]         = useState('#1a1a1a');
  const [activeBrushSize,     setActiveBrushSize]     = useState(5);
  const [shapesMenuOpen,      setShapesMenuOpen]      = useState(false);
  const [textMenuOpen,        setTextMenuOpen]        = useState(false);
  const [fontSize,            setFontSize]            = useState(16);
  const [bold,                setBold]                = useState(false);
  const [italic,              setItalic]              = useState(false);
  const [closeBtnHov,         setCloseBtnHov]         = useState(false);
  const [saveBtnHov,          setSaveBtnHov]          = useState(false);
  const [shapeBtnHov,         setShapeBtnHov]         = useState(false);
  const [textBtnHov,          setTextBtnHov]          = useState(false);

  const shapesMenuRef = useRef(null);
  const textMenuRef   = useRef(null);

  useOutsideClick(shapesMenuRef, 'data-shapes-btn', () => setShapesMenuOpen(false), shapesMenuOpen);
  useOutsideClick(textMenuRef,   'data-text-btn',   () => setTextMenuOpen(false),   textMenuOpen);

  // ── Tracking de l'IText en cours d'édition ────────────────────────────────
  // text:editing:entered / exited sont fiables même quand le canvas perd le focus
  // suite à un clic sur un bouton de la toolbar.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onEnter = (e) => { activeITextRef.current = e.target ?? canvas.getActiveObject(); };
    const onExit  = ()  => { activeITextRef.current = null; };
    canvas.on('text:editing:entered', onEnter);
    canvas.on('text:editing:exited',  onExit);
    return () => {
      canvas.off('text:editing:entered', onEnter);
      canvas.off('text:editing:exited',  onExit);
    };
  }, [canvasRef]);

  // Ferme le menu formes au clic canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handler = () => setShapesMenuOpen(false);
    canvas.on('mouse:down', handler);
    return () => canvas.off('mouse:down', handler);
  }, [canvasRef]);

  // ── Dérivés ───────────────────────────────────────────────────────────────
  const currentBoard = whiteboardList.find(wb => wb._id === currentWhiteboardId);
  const isOwner   = !createdBy || createdBy === userId;
  const isAuteur  = !currentWhiteboardId ||
    String(currentBoard?.createdBy?._id || currentBoard?.createdBy) === String(userId);
  const canChangeVisibility = isOwner;

  const activeShape  = ['rect','circle','line','arrow'].includes(mode) ? mode : null;
  const isTextMode   = mode === 'text';
  const isSelectMode = mode === 'select';
  const isDrawMode   = mode === 'draw';

  // ── Getters stables pour les hooks ────────────────────────────────────────
  const getColor     = useCallback(() => activeColor,     [activeColor]);
  const getBrushSize = useCallback(() => activeBrushSize, [activeBrushSize]);
  const getTextOpts  = useCallback(() => ({ color: activeColor, fontSize, bold, italic }),
                                   [activeColor, fontSize, bold, italic]);

  // ── Hooks formes + texte ──────────────────────────────────────────────────
  useWhiteboardShapes(canvasRef, activeShape, getColor, getBrushSize, socket, discussionId, userId);
  useWhiteboardText(canvasRef, isTextMode, getTextOpts, () => setTextMenuOpen(false), socket, discussionId, userId);

  // ── Synchronisation canvas selon le mode ─────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (mode === 'draw') {
      canvas.isDrawingMode = true;
      canvas.selection     = false;
      if (canvas.freeDrawingBrush) {
        canvas.freeDrawingBrush.color = activeColor;
        canvas.freeDrawingBrush.width = activeBrushSize;
      } else {
        onSetPencil(activeColor, activeBrushSize);
      }
    } else if (mode === 'select') {
      canvas.isDrawingMode = false;
      canvas.selection     = true;
    } else if (mode === 'erase') {
      onSetEraser();
    }
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Application de styles texte à la volée ────────────────────────────────
  // Utilise activeITextRef pour retrouver l'IText même après perte de focus.
  const applyTextStyle = useCallback((styles) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Récupère l'IText mémorisé ou l'objet actif courant
    const obj = activeITextRef.current ?? canvas.getActiveObject();
    if (!obj || (obj.type !== 'i-text' && obj.type !== 'textbox')) return;

    const wasEditing   = obj.isEditing;
    const selStart     = obj.selectionStart ?? 0;
    const selEnd       = obj.selectionEnd   ?? 0;
    const hasSelection = wasEditing && selStart !== selEnd;

    if (hasSelection) {
      // Applique uniquement aux caractères sélectionnés
      const charStyles = {};
      if (styles.fill)       charStyles.fill       = styles.fill;
      if (styles.fontSize)   charStyles.fontSize   = styles.fontSize;
      if (styles.fontWeight) charStyles.fontWeight = styles.fontWeight;
      if (styles.fontStyle)  charStyles.fontStyle  = styles.fontStyle;
      obj.setSelectionStyles(charStyles, selStart, selEnd);
    } else {
      // Applique à l'objet entier
      if (styles.fill)       obj.set('fill',       styles.fill);
      if (styles.fontSize)   obj.set('fontSize',   styles.fontSize);
      if (styles.fontWeight) obj.set('fontWeight', styles.fontWeight);
      if (styles.fontStyle)  obj.set('fontStyle',  styles.fontStyle);
    }

    obj.dirty = true;
    obj.initDimensions?.();
    obj.setCoords();
    canvas.requestRenderAll();

    // Ré-entre en édition et restaure la position du curseur
    canvas.setActiveObject(obj);
    requestAnimationFrame(() => {
      obj.enterEditing();
      if (hasSelection) {
        obj.selectionStart = selStart;
        obj.selectionEnd   = selEnd;
      }
      canvas.requestRenderAll();
    });
  }, [canvasRef]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const setModeTo = (m) => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.getObjects().forEach(obj => {
        if ((obj.type === 'i-text' || obj.type === 'textbox') && obj.isEditing)
          obj.exitEditing();
      });
      canvas.discardActiveObject();
      canvas.renderAll();
    }
    activeITextRef.current = null;
    setShapesMenuOpen(false);
    setTextMenuOpen(false);
    setMode(m);
  };

  const changeColor = (hex) => {
    setActiveColor(hex);
    if (mode === 'draw') onSetPencil(hex, activeBrushSize);
    if (mode === 'text') applyTextStyle({ fill: hex });
  };

  const changeBrushSize = (size) => {
    setActiveBrushSize(size);
    const canvas = canvasRef.current;
    if (canvas?.freeDrawingBrush) {
      canvas.freeDrawingBrush.width = size;
      canvas.freeDrawingBrush.color = activeColor;
    }
  };

  const clearCanvas = async () => {
    if (!canvasRef.current) return;
    const confirmed = await prompt.confirm(t('tableau.clear'));
    if (!confirmed) return;
    canvasRef.current.clear();
    canvasRef.current.backgroundColor = '#ffffff';
    canvasRef.current.renderAll();
    socket.emit('clear-whiteboard', { discussionId });
  };

  const deleteSelected = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const obj = canvas.getActiveObject();
    if (obj) { canvas.remove(obj); canvas.discardActiveObject(); canvas.renderAll(); }
    setModeTo('draw');
  };

  const handleVisibilityChange = async (v) => {
    if (!isAuteur) { await prompt.alert(t('tableau.onlyOwnerCanChangeVisibility')); return; }
    onSetVisibility(v);
  };

  const confirmation = async () => {
    const confirmed = await prompt.confirm(t('tableau.leave'));
    if (!confirmed) return;
    onClose();
  };

  const handleSelectChange = (e) => {
    if (e.target.value === '__new__') {
      setCurrentWhiteboardId(null);
      setCreateur(null);
      onNew();
    } else {
      setCurrentWhiteboardId(e.target.value);
      onSelect(e.target.value);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const shapeBtnActive = !!activeShape || shapesMenuOpen;
  const shapeBtnColor  = shapeBtnActive ? '#1d4ed8' : shapeBtnHov ? '#111' : '#555';
  const textBtnActive  = isTextMode || textMenuOpen;
  const textBtnColor   = textBtnActive ? '#1d4ed8' : textBtnHov ? '#111' : '#555';

  return (
    <div style={S.wrap}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={S.header}>
        <div style={S.headerLeft}>
          <div style={S.iconBox}>
            <Icon d={ICONS.pencil} size={18} color="#1d4ed8"/>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={S.titleRow}>
              <span style={S.titleText}>{t('tableau.titre')}</span>
              <button style={S.infoBtn} aria-label="Informations"
                onClick={() => prompt.alert(<span dangerouslySetInnerHTML={{ __html: t('tableau.explication_tableau') }}/>)}>
                <Icon d={ICONS.infoCircle} size={16} color="#888"/>
              </button>
            </div>
            {discussionName && createur && (
              <span style={S.subtitle}>
                <Icon d={ICONS.user} size={11} color="#888"/>
                {createur} — {discussionName}
              </span>
            )}
          </div>
        </div>

        <button
          style={{ background: closeBtnHov ? '#fee2e2' : 'none', border: 'none', cursor: 'pointer',
                   width: 28, height: 28, borderRadius: 7, display: 'flex', alignItems: 'center',
                   justifyContent: 'center', flexShrink: 0, transition: 'background .15s' }}
          aria-label="Fermer" onClick={confirmation}
          onMouseEnter={() => setCloseBtnHov(true)} onMouseLeave={() => setCloseBtnHov(false)}>
          <Icon d={ICONS.x} size={16} color={closeBtnHov ? '#b91c1c' : '#888'}/>
        </button>
      </div>

      {/* ── Visibilité ────────────────────────────────────────────────── */}
      <div style={S.visRow} role="group">
        <button style={S.visBtn(visibility === 'shared', 'shared')}
          onClick={() => handleVisibilityChange('shared')} disabled={!canChangeVisibility}>
          <Icon d={ICONS.world} size={13} color={visibility === 'shared' ? '#1d4ed8' : '#666'}/>
          {t('agenda.shared')}
        </button>
        {isAuteur && (
          <button style={S.visBtn(visibility === 'private', 'private')}
            onClick={() => handleVisibilityChange('private')}>
            <Icon d={ICONS.lock} size={13} color={visibility === 'private' ? '#555' : '#666'}/>
            {t('agenda.private')}
          </button>
        )}
      </div>

      {/* ── Toolbar ───────────────────────────────────────────────────── */}
      <div style={S.toolbar} role="toolbar" aria-label="Outils">

        {/* Sélecteur */}
        <select style={S.select(130)} onChange={handleSelectChange}
          value={currentWhiteboardId || '__placeholder__'}>
          <option value="__placeholder__" disabled>{currentName || 'Mes tableaux'}</option>
          <option value="__new__">✚ {t('tableau.new')}</option>
          {whiteboardList.map(wb => (
            <option key={wb._id} value={wb._id}>
              {wb.name} — {new Date(wb.updatedAt).toLocaleDateString()}
            </option>
          ))}
        </select>

        <div style={S.sep}/>

        {/* Couleurs */}
        {COLORS.map(({ hex, label }) => (
          <span key={hex} role="button" tabIndex={0} aria-label={label}
            style={S.colorDot(hex, activeColor === hex)}
            onClick={() => changeColor(hex)}
            onKeyDown={e => e.key === 'Enter' && changeColor(hex)}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}/>
        ))}

        <div style={S.sep}/>

        {/* Taille pinceau */}
        <select style={S.select(68)} value={activeBrushSize}
          onChange={e => changeBrushSize(parseInt(e.target.value))}>
          <option value={2}>2 px</option>
          <option value={5}>5 px</option>
          <option value={10}>10 px</option>
        </select>

        <div style={S.sep}/>

        {/* Outils principaux */}
        <ToolButton iconKey="cursor"      label="Mode sélection" active={isSelectMode} onClick={() => setModeTo('select')}/>
        <ToolButton iconKey="pencil"      label="Mode dessin"    active={isDrawMode}   onClick={() => setModeTo('draw')}/>
        <ToolButton iconKey="eraser"      label="Gomme"                                onClick={() => setModeTo('erase')}/>
        <ToolButton iconKey="arrowBackUp" label="Annuler"                              onClick={onUndo}/>
        <ToolButton iconKey="scissors"    label="Supprimer la sélection"               onClick={deleteSelected}/>

        {/* Image */}
        <input ref={fileInputRef} type="file" accept="image/*" onChange={loadImage} style={{ display: 'none' }}/>
        <ToolButton iconKey="photoPlus" label="Insérer une image" onClick={() => fileInputRef.current?.click()}/>

        <div style={S.sep}/>

        {/* ── Formes ── */}
        <div style={{ position: 'relative' }}>
          <button data-shapes-btn
            style={S.dropBtn(shapeBtnActive, shapeBtnHov)}
            aria-label="Formes" title="Formes"
            onClick={() => setShapesMenuOpen(v => !v)}
            onMouseEnter={() => setShapeBtnHov(true)}
            onMouseLeave={() => setShapeBtnHov(false)}>
            <Icon d={ICONS[activeShape ?? 'shapes']} size={17} color={shapeBtnColor}/>
            <Icon d={ICONS.chevronDown} size={11} color={shapeBtnColor} style={{ marginLeft: 1 }}/>
          </button>

          {shapesMenuOpen && (
            <div ref={shapesMenuRef} style={{ ...S.dropMenu, ...S.shapeGrid }} role="menu">
              {SHAPES.map(shape => (
                <ShapeItemBtn key={shape.key} shape={shape} active={activeShape === shape.key}
                  onClick={() => { setModeTo(shape.key); setShapesMenuOpen(false); }}/>
              ))}
            </div>
          )}
        </div>

        {/* ── Texte ── */}
        <div style={{ position: 'relative' }}>
          <button data-text-btn
            style={S.dropBtn(textBtnActive, textBtnHov)}
            aria-label="Texte" title="Texte"
            onClick={() => {
              setModeTo('text');
              setTextMenuOpen(v => !v);
            }}
            onMouseEnter={() => setTextBtnHov(true)}
            onMouseLeave={() => setTextBtnHov(false)}>
            <Icon d={ICONS.textCursor} size={17} color={textBtnColor}/>
            <Icon d={ICONS.chevronDown} size={11} color={textBtnColor} style={{ marginLeft: 1 }}/>
          </button>

          {textMenuOpen && (
            <div ref={textMenuRef} style={{ ...S.dropMenu, ...S.textMenuInner }} role="menu">

              <div>
                <div style={S.textLabel}>Taille</div>
                <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                  {FONT_SIZES.map(s => (
                    <FontSizeBtn key={s} size={s} active={fontSize === s}
                      onClick={v => { setFontSize(v); applyTextStyle({ fontSize: v }); }}/>
                  ))}
                </div>
              </div>

              <div>
                <div style={S.textLabel}>Style</div>
                <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                  <ToggleBtn iconKey="bold"   label="Gras"     active={bold}
                    onClick={() => { const v = !bold;   setBold(v);   applyTextStyle({ fontWeight: v ? 'bold'   : 'normal' }); }}/>
                  <ToggleBtn iconKey="italic" label="Italique" active={italic}
                    onClick={() => { const v = !italic; setItalic(v); applyTextStyle({ fontStyle:  v ? 'italic' : 'normal' }); }}/>
                </div>
              </div>

            </div>
          )}
        </div>

        <div style={S.sep}/>

        {/* Groupe droite */}
        <div style={S.rightGroup}>
          <ToolButton iconKey="trash" label={t('tableau.vider') || 'Vider'} danger onClick={clearCanvas}/>
          {isAuteur && (
            <ToolButton iconKey="fileMinus" label={t('agenda.sup') || 'Supprimer'} danger onClick={onDelete}/>
          )}
          <button style={S.saveBtn(saveBtnHov)} onClick={onSave} aria-label="Sauvegarder"
            onMouseEnter={() => setSaveBtnHov(true)} onMouseLeave={() => setSaveBtnHov(false)}>
            <Icon d={ICONS.floppy} size={15} color="#166534"/>
            {t('agenda.save') || 'Sauvegarder'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default WhiteboardToolbar;
